import React from 'react';

import Avatar from 'common-ui/chat/Avatar';
import { PLATFORM_CONFIG, formatChatListUnreadCount } from 'common-ui/chat/constants';

import {
  AccountsContainer,
  AccountsSection,
  PlatformIcon,
  PlatformIconBlock,
  PlatformIconWrapper,
  PlatformItem,
  PlatformName,
  PlatformsBlock,
  UnreadBadge,
  UserAccountItem,
  UserAccountsGrid,
} from './PlatformAccountsSection.styles';

const PlatformAccountsSection = ({
  platforms = [],
  permissions,
  conversations = [],
  selectedPlatform,
  onPlatformSelect,
  onUserClick,
}) => {
  const getAllowedPlatforms = () => {
    if (!permissions?.permissions) return [];

    return permissions.permissions
      .filter(
        (perm) => !perm.isDenied && PLATFORM_CONFIG[perm.platform] && perm.platform !== 'gmail'
      )
      .map((perm) => perm.platform);
  };

  const getPlatformData = (platformId) => {
    return platforms.find((p) => p.id === platformId);
  };

  // Extract unique users from conversations with unread messages
  const getUniqueUsersWithUnread = () => {
    const usersMap = new Map();

    conversations.forEach((conversation) => {
      if (conversation.sender?.id && conversation.unreadCount > 0) {
        const userId = conversation.sender.id;
        if (!usersMap.has(userId)) {
          usersMap.set(userId, {
            id: userId,
            avatar: conversation.sender.avatar,
            name: conversation.sender.name,
            surname: conversation.sender.surname,
            unreadCount: conversation.unreadCount,
            platform: selectedPlatform || 'facebook',
            conversation: conversation, // Store the conversation reference
          });
        } else {
          // If user already exists, add to their unread count
          const existingUser = usersMap.get(userId);
          existingUser.unreadCount += conversation.unreadCount;
        }
      }
    });

    return Array.from(usersMap.values());
  };

  const allowedPlatforms = getAllowedPlatforms();
  const uniqueUsersWithUnread = getUniqueUsersWithUnread();

  if (allowedPlatforms.length === 0) {
    return null;
  }

  return (
    <AccountsSection>
      <AccountsContainer>
        {/* Platforms Block - matching ChatBadges style */}
        <PlatformsBlock>
          {allowedPlatforms.map((platformId) => {
            const config = PLATFORM_CONFIG[platformId];
            const platformData = getPlatformData(platformId);
            const unreadCount = platformData?.unreadCount || 0;
            const isSelected = selectedPlatform === platformId;

            return (
              <PlatformItem
                key={platformId}
                className={isSelected ? 'active' : ''}
                onClick={() => onPlatformSelect && onPlatformSelect(platformId)}
              >
                <PlatformIconBlock>
                  {config.bg ? (
                    <PlatformIconWrapper $bg={config.bg}>
                      <img src={config.icon} alt={config.name} />
                    </PlatformIconWrapper>
                  ) : (
                    <PlatformIcon src={config.icon} />
                  )}
                  {unreadCount > 0 && (
                    <UnreadBadge className={`platform-badge ${config.platformClass}`}>
                      {formatChatListUnreadCount(unreadCount)}
                    </UnreadBadge>
                  )}
                </PlatformIconBlock>
                <PlatformName>{config.name}</PlatformName>
              </PlatformItem>
            );
          })}
        </PlatformsBlock>

        {/* User Avatars - Only users with unread messages */}
        {uniqueUsersWithUnread.length > 0 && (
          <UserAccountsGrid>
            {uniqueUsersWithUnread.map((user) => {
              const userName =
                user.name && user.surname
                  ? `${user.name} ${user.surname}`
                  : user.name || `User ${user.id.slice(-6)}`;

              return (
                <UserAccountItem
                  key={user.id}
                  onClick={() => onUserClick && onUserClick(user.conversation)}
                >
                  <Avatar
                    name={userName}
                    src={user.avatar}
                    platform={user.platform}
                    size={30}
                  />
                  <UnreadBadge className="platform-badge user-badge">
                    {formatChatListUnreadCount(user.unreadCount)}
                  </UnreadBadge>
                </UserAccountItem>
              );
            })}
          </UserAccountsGrid>
        )}
      </AccountsContainer>
    </AccountsSection>
  );
};

export default PlatformAccountsSection;
